import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query'
import { showSessionExpiredToast as raiseSessionExpiredToast } from '@/lib/session-expired-toast'

/**
 * Detects whether an error represents an authentication (not authorization) failure
 * that should surface the "session expired" UX.
 *
 * Unambiguous auth signals:
 * - PostgREST code `PGRST301` — JWT expired/invalid
 * - supabase-js `AuthApiError` (carries `__isAuthError: true`)
 * - error message contains "JWT expired" / "Invalid JWT"
 *
 * HTTP 401 is treated as auth ONLY when it lacks a PostgREST error code, so a
 * 401 paired with a permission-level code (e.g. `42501` insufficient_privilege)
 * does not get mis-surfaced as "session expired". Our edge functions that
 * return a bare 401 do so exclusively for missing/invalid JWTs.
 */
function isAuthError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const e = error as {
    status?: number
    code?: string
    message?: string
    __isAuthError?: boolean
  }
  if (e.code === 'PGRST301') return true
  if (e.__isAuthError === true) return true
  if (typeof e.message === 'string' && /JWT expired|Invalid JWT/i.test(e.message)) return true
  if (e.status === 401 && !e.code) return true
  return false
}

/**
 * Shows the persistent session-expired toast and forces navigation to /login.
 *
 * The `id` dedupes repeated events (e.g. multiple 401s in flight) into one toast.
 * This fires as a fallback when AuthContext's SIGNED_OUT listener misses the
 * event (supabase-js #41968 — occasional skipped SIGNED_OUT on background
 * requests). queryClient has no router access, so we use window.location;
 * the pathname guard prevents a reload loop if we're already on /login.
 */
function showSessionExpiredToast(): void {
  raiseSessionExpiredToast()
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.href = '/login'
  }
}

/**
 * Centralized React Query client.
 *
 * Global behavior:
 * - Skip retries on 4xx errors (401/403/404 will not succeed on retry).
 * - Fallback session-expired toast from query/mutation cache onError — the
 *   AuthContext SIGNED_OUT listener is the primary trigger, but supabase-js
 *   can occasionally miss SIGNED_OUT for background requests (see
 *   supabase-js issue #41968). The toast `id` dedupes with the AuthContext toast.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes — preserved from previous main.tsx config
      retry: (failureCount, error) => {
        const status = (error as { status?: number } | null)?.status
        // Do not retry 4xx — auth/authorization/not-found won't become successes.
        if (typeof status === 'number' && status >= 400 && status < 500) return false
        return failureCount < 1
      },
      refetchOnWindowFocus: false, // preserved from previous main.tsx config
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      if (isAuthError(error)) showSessionExpiredToast()
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      if (isAuthError(error)) showSessionExpiredToast()
    },
  }),
})
